import { FormEvent, MouseEventHandler, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSandboxModalFields } from 'SandboxModule/hooks/useSandboxModalFields';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'selectors';
import { useRealmIdGeneration } from 'SandboxModule/hooks/useRealmIdGeneration';
import { useCreateRealm } from 'SandboxModule/hooks/useCreateRealm';
import { useTenantDropdown } from 'TenantModule/hooks/useTenantDropdown';
import { RealmTypes } from 'RealmModule/constants/realm';
import { IntegrationAgentNames } from '@constants';
import { INTEGRATION_SERVICE_NAMES } from '@constants/integration';
import { useIntegrationAgentsMultiSelector } from 'IntegrationModule/components/IntegrationAgentsMultiSelector/useIntegrationAgentsMultiSelector';
import { useIntegrationDisplayNamesQuery } from 'IntegrationModule/api/queries/useIntegrationDisplayNamesQuery';
import { useNotification } from 'hooks/useNotification';

export interface IntegrationAgentSecretMappingItem {
  integrationAgentName: IntegrationAgentNames | string;
  secret: string;
  id: string;
}

interface SubscriptionRealmFields {
  teamName: string;
  realmType: string;
  isFirstRealm?: boolean;
  integrationAgentName?: IntegrationAgentNames;
}

const createSubscriptionRealmFieldsInitialValues = {
  teamName: '',
  realmType: RealmTypes.subscription,
  isFirstRealm: false,
};

const defaultRegion = 'us-east-1';
const defaultDomain = 'mosaicapp.com';

export const useCreateSubscriptionRealmForm = ({
  showAdvancedFields = false,
  onSuccess,
}: {
  showAdvancedFields?: boolean;
  onSuccess?: VoidFunction;
}) => {
  const [createSubscriptionRealmFields, setCreateSubscriptionRealmFields] =
    useState<SubscriptionRealmFields>(createSubscriptionRealmFieldsInitialValues);
  const {
    handleTagCheckboxChange,
    standardTags,
    tagsList,
    applyTagsChange,
    tagError,
    teamNameRef,
  } = useSandboxModalFields();
  const user = useSelector(selectAuthUser);
  const { showInfo } = useNotification();
  const { integrationDisplayNames } = useIntegrationDisplayNamesQuery();
  const integrationAgentsSelectorProps = useIntegrationAgentsMultiSelector({
    integrationAgentsToExclude: [INTEGRATION_SERVICE_NAMES.CSV_AGENT],
  });
  const { openTenantDropdown, selectedTenant } = useTenantDropdown();

  const { createRealm, isCreatingRealm } = useCreateRealm({
    onSuccess,
  });

  const generateRealmIdOnSuccess: Parameters<typeof useRealmIdGeneration>[0]['onSuccess'] = ({
    response,
  }) => {
    const realmId = response.data;
    const additionalServices = integrationAgentsSelectorProps.selectedIntegrationAgents;

    const adminCredentials = integrationAgentNamesAndSecrets.reduce<Record<string, string>>(
      (acc, item) => {
        acc[item.integrationAgentName] = item.secret;
        return acc;
      },
      {},
    );

    if (realmId && user && selectedTenant) {
      const params = {
        realmId,
        teamName: createSubscriptionRealmFields.teamName,
        domain: defaultDomain,
        region: defaultRegion,
        realmType: RealmTypes.subscription,
        tenantName: selectedTenant.tenantName,
        tags: tagsList,
        metaPayload: { name: user.account.email }, // Intentionally using the account email instead of the name to ensure accuracy
        ...(additionalServices.length && {
          additionalServices,
        }),
        ...(showAdvancedFields && {
          adminCredentials,
          isFirstRealm: createSubscriptionRealmFields.isFirstRealm,
        }),
      };

      // ✅ FIX: Replace alert() with toast notification
      showInfo(
        `Initiating creating new subscription: ${createSubscriptionRealmFields.teamName}. It will take a while.`,
      );
      createRealm(params);
    }
  };

  const { generateRealmId, isGeneratingRealmId } = useRealmIdGeneration({
    onSuccess: generateRealmIdOnSuccess,
  });

  const [integrationAgentNamesAndSecrets, setIntegrationAgentNamesAndSecrets] = useState<
    IntegrationAgentSecretMappingItem[]
  >([]);

  const selectedIntegrationAgentNamesSet = new Set(
    integrationAgentNamesAndSecrets.map((item) => item.integrationAgentName),
  );

  const handleIntegrationAgentsSecretsChange = ({
    name,
    value,
    id,
  }: {
    name: string;
    value: string;
    id: string;
  }) => {
    setIntegrationAgentNamesAndSecrets((prev) => {
      return prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [name]: value,
            }
          : item,
      );
    });
  };

  const onAddNewIntegrationSecret = () => {
    const uuid = uuidv4();
    const newIntegrationSecret = {
      integrationAgentName: '',
      secret: '',
      id: uuid,
    };

    setIntegrationAgentNamesAndSecrets((prev) => [...prev, newIntegrationSecret]);
  };

  const removeIntegrationSecretsRow = (id: string) => {
    const newState = integrationAgentNamesAndSecrets.filter((item) => item.id !== id);
    setIntegrationAgentNamesAndSecrets(newState);
  };

  const onCreateSubscriptionRealmFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target;
    if (type === 'checkbox') {
      const isChecked = event.target.checked;
      setCreateSubscriptionRealmFields((prev) => ({
        ...prev,
        [name]: isChecked,
      }));
    } else {
      setCreateSubscriptionRealmFields((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    openTenantDropdown({
      target: e.currentTarget,
    });
  };

  // only allow to add each integration once
  const integrationAgentsOverride = integrationDisplayNames.map((item) => {
    const disabled = selectedIntegrationAgentNamesSet.has(item.nonDisplayName);
    return { value: item.nonDisplayName, name: item.displayName, disabled };
  });

  const onSubmitCreateSubscriptionRealmForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    generateRealmId({ teamName: createSubscriptionRealmFields.teamName });
  };

  const onPressEnter = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return {
    createSubscriptionRealmFields,
    onCreateSubscriptionRealmFormChange,
    onSubmitCreateSubscriptionRealmForm,
    onPressEnter,
    isRequesting: isCreatingRealm || isGeneratingRealmId,
    showAdvancedFields,
    selectedTenant,
    onTenantSelectorClick,
    handleTagCheckboxChange,
    standardTags,
    tagsList,
    applyTagsChange,
    tagError,
    teamNameRef,
    integrationAgentNamesAndSecrets,
    onAddNewIntegrationSecret,
    removeIntegrationSecretsRow,
    handleIntegrationAgentsSecretsChange,
    integrationAgentsOverride,
    integrationAgentsSelectorProps,
  };
};
